import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, InputAdornment, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import * as S from "./styles";
import UserAvatar from "../userAvatar";
import ContactSheet from "../contactSheet";
import { IContact } from "../../utils/services/modules/contacts";

interface ContactsProps {
  contacts?: IContact[]; // undefined enquanto o usuário carrega
  onAddContact: () => void;
}

// A busca local só aparece quando a lista começa a ficar grande
const FILTER_MIN_CONTACTS = 5;

// sem acentos e em minúsculas, para "joao" encontrar "João"
const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const ContactsList: React.FC<ContactsProps> = ({ contacts, onAddContact }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<IContact | null>(null);

  const sorted = useMemo(
    () =>
      [...(contacts ?? [])].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "pt-BR", {
          sensitivity: "base",
        }),
      ),
    [contacts],
  );

  const visible = useMemo(() => {
    const term = normalize(filter.trim());
    if (!term) return sorted;
    return sorted.filter(
      (contact) =>
        normalize(contact.name ?? "").includes(term) ||
        String(contact.code).includes(term),
    );
  }, [sorted, filter]);

  const handleSend = (code: number) => navigate(`/wallet?code=${code}`);

  if (!contacts) {
    return (
      <S.Container aria-busy>
        {[0, 1, 2].map((i) => (
          <S.SkeletonCard key={i}>
            <Skeleton variant="circular" width={44} height={44} />
            <S.SkeletonText>
              <Skeleton width="55%" height={20} />
              <Skeleton width="25%" height={16} />
            </S.SkeletonText>
          </S.SkeletonCard>
        ))}
      </S.Container>
    );
  }

  if (contacts.length === 0) {
    return (
      <S.Empty>
        <S.EmptyIcon />
        <S.EmptyTitle>Você ainda não tem contatos</S.EmptyTitle>
        <S.EmptyText>
          Adicione pessoas pelo código de 4 números para enviar moedas com
          poucos toques.
        </S.EmptyText>
        <S.PrimaryButton type="button" onClick={onAddContact}>
          <S.AddIcon />
          Adicionar contato
        </S.PrimaryButton>
      </S.Empty>
    );
  }

  return (
    <S.Container>
      {contacts.length >= FILTER_MIN_CONTACTS && (
        <S.FilterInput
          placeholder="Buscar por nome ou ID"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          inputProps={{
            maxLength: 50,
            enterKeyHint: "search",
            "aria-label": "Buscar contato",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#999999" }} />
              </InputAdornment>
            ),
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Limpar busca"
                  onClick={() => setFilter("")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      )}

      {visible.length === 0 ? (
        <S.NoResults>Nenhum contato encontrado para "{filter.trim()}".</S.NoResults>
      ) : (
        <S.List>
          {visible.map((contact, index) => (
            <S.ContactCard
              key={contact.code}
              $index={index}
              role="button"
              tabIndex={0}
              aria-label={`Ver ${contact.name}`}
              onClick={() => setSelected(contact)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSelected(contact);
              }}
            >
              <UserAvatar
                name={contact.name}
                code={contact.code}
                avatar={contact.avatar}
              />
              <S.InfoGroup>
                <S.Name>{contact.name}</S.Name>
                <S.SubInfo>ID {contact.code}</S.SubInfo>
              </S.InfoGroup>
              <S.SendButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend(contact.code);
                }}
              >
                <S.SendIcon />
                Enviar
              </S.SendButton>
            </S.ContactCard>
          ))}
        </S.List>
      )}

      <S.Hint>Toque em um contato para ver os detalhes ou removê-lo.</S.Hint>

      <ContactSheet
        contact={selected}
        onClose={() => setSelected(null)}
        onSend={handleSend}
      />
    </S.Container>
  );
};

export default ContactsList;
